// ============================================================================
// USE FACET CONFIG HOOK
// Manages account-level facet category configuration with caching
// ============================================================================

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import {
  facetConfigApi,
  type FacetCategoryConfigItem,
  type FacetTypeMappingItem,
  type FacetConfigResponse,
} from '@/lib/api/client'
import { FACET_TYPE_CONFIG, FACET_CATEGORY_CONFIG, type FacetCategory } from '@/types/interviewer'

// ============================================================================
// TYPES
// ============================================================================

export interface FacetConfigState {
  // API returns objects keyed by ID
  categories: Record<string, FacetCategoryConfigItem>
  mappings: Record<string, FacetTypeMappingItem>
  isDefault: boolean
  loading: boolean
  error: string | null
}

export interface FacetStyleConfig {
  category: FacetCategory
  categoryLabel: string
  categoryColor: string
  categoryIcon: string
  facetLabel: string
  facetColor: string
  facetIcon: string
  createsEntity: boolean
  defaultEntityType: string | null
  allowedEntityTypes: string[] | null
}

// ============================================================================
// CACHE
// ============================================================================

// Simple in-memory cache to avoid re-fetching config on every component mount
const cache: Map<string, { data: FacetConfigResponse; timestamp: number }> = new Map()
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

function getCachedConfig(accountId: string): FacetConfigResponse | null {
  const cached = cache.get(accountId)
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data
  }
  return null
}

function setCachedConfig(accountId: string, data: FacetConfigResponse): void {
  cache.set(accountId, { data, timestamp: Date.now() })
}

function invalidateCache(accountId: string): void {
  cache.delete(accountId)
}

// ============================================================================
// HOOK
// ============================================================================

export function useFacetConfig() {
  const { accountId, projectId } = useSessionStore()
  const [state, setState] = useState<FacetConfigState>({
    categories: {},
    mappings: {},
    isDefault: true,
    loading: true,
    error: null,
  })

  // Load configuration
  const loadConfig = useCallback(async (forceRefresh = false) => {
    if (!accountId || !projectId) {
      setState(prev => ({ ...prev, loading: false, error: projectId ? null : 'No project selected' }))
      return
    }

    // Check cache first (using projectId as key since config is project-scoped)
    const cacheKey = `${accountId}:${projectId}`
    if (!forceRefresh) {
      const cached = getCachedConfig(cacheKey)
      if (cached) {
        setState({
          categories: cached.categories,
          mappings: cached.mappings,
          isDefault: !cached.isCustomized,
          loading: false,
          error: null,
        })
        return
      }
    }

    setState(prev => ({ ...prev, loading: true, error: null }))

    try {
      // Facet config is now project-scoped
      const config = await facetConfigApi.get(accountId, projectId)
      setCachedConfig(cacheKey, config)
      setState({
        categories: config.categories,
        mappings: config.mappings,
        isDefault: !config.isCustomized,
        loading: false,
        error: null,
      })
    } catch (err) {
      // If API fails, use static defaults from interviewer.ts
      setState({
        categories: {},
        mappings: {},
        isDefault: true,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load facet config',
      })
    }
  }, [accountId, projectId])

  // Initialize account with defaults
  const initializeConfig = useCallback(async () => {
    if (!accountId || !projectId) return

    setState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const config = await facetConfigApi.initialize(accountId, projectId)
      const cacheKey = `${accountId}:${projectId}`
      setCachedConfig(cacheKey, config)
      setState({
        categories: config.categories,
        mappings: config.mappings,
        isDefault: !config.isCustomized,
        loading: false,
        error: null,
      })
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to initialize config',
      }))
    }
  }, [accountId, projectId])

  // Reset to defaults
  const resetConfig = useCallback(async () => {
    if (!accountId || !projectId) return

    setState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const config = await facetConfigApi.reset(accountId, projectId)
      const cacheKey = `${accountId}:${projectId}`
      setCachedConfig(cacheKey, config)
      setState({
        categories: config.categories,
        mappings: config.mappings,
        isDefault: !config.isCustomized,
        loading: false,
        error: null,
      })
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to reset config',
      }))
    }
  }, [accountId, projectId])

  // Refresh config from server
  const refresh = useCallback(() => {
    if (accountId && projectId) {
      const cacheKey = `${accountId}:${projectId}`
      invalidateCache(cacheKey)
    }
    return loadConfig(true)
  }, [accountId, projectId, loadConfig])

  // Load on mount and when accountId changes
  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  // ============================================================================
  // DERIVED DATA - Merge API config with static fallbacks
  // ============================================================================

  // Build category lookup from API or fallback to static config
  const categoryConfig = useMemo(() => {
    const result: Record<string, {
      id: string
      label: string
      color: string
      icon: string
      sortOrder: number
    }> = {}

    // Start with static defaults
    Object.entries(FACET_CATEGORY_CONFIG).forEach(([key, cfg]) => {
      result[key] = {
        id: key,
        label: cfg.label,
        color: cfg.color,
        icon: cfg.icon,
        sortOrder: cfg.sortOrder || 0,
      }
    })

    // Override with API config if available (categories is now an object)
    Object.values(state.categories).forEach(cat => {
      result[cat.categoryId] = {
        id: cat.categoryId,
        label: cat.label,
        color: cat.color,
        icon: cat.icon,
        sortOrder: cat.sortOrder,
      }
    })

    return result
  }, [state.categories])

  // Build facet type lookup from API or fallback to static config
  const facetTypeConfig = useMemo(() => {
    const result: Record<string, {
      category: FacetCategory
      label: string
      color: string
      icon: string
      createsEntity: boolean
      defaultEntityType: string | null
      allowedEntityTypes: string[] | null
    }> = {}

    // Start with static defaults
    Object.entries(FACET_TYPE_CONFIG).forEach(([key, cfg]) => {
      // Determine if this facet type creates entities based on ontologyTypes
      const hasOntologyTypes = cfg.ontologyTypes && cfg.ontologyTypes.length > 0
      const defaultEntityType = hasOntologyTypes ? cfg.ontologyTypes[0] : null

      result[key] = {
        category: cfg.category,
        label: cfg.label,
        color: cfg.color,
        icon: cfg.icon,
        createsEntity: hasOntologyTypes,
        defaultEntityType,
        allowedEntityTypes: hasOntologyTypes ? cfg.ontologyTypes : null,
      }
    })

    // Override with API config if available (mappings is now an object)
    Object.values(state.mappings).forEach(mapping => {
      result[mapping.facetTypeId] = {
        category: mapping.categoryId as FacetCategory,
        label: mapping.label,
        color: mapping.color || categoryConfig[mapping.categoryId]?.color || '#6c757d',
        icon: mapping.icon || 'FileText',
        createsEntity: mapping.createsEntity,
        defaultEntityType: mapping.defaultEntityType ?? null,
        allowedEntityTypes: mapping.allowedEntityTypes ?? null,
      }
    })

    return result
  }, [state.mappings, categoryConfig])

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Get full style configuration for a facet type
   * Falls back to static config if API config not available
   */
  const getFacetStyle = useCallback((facetTypeId: string): FacetStyleConfig => {
    const typeConfig = facetTypeConfig[facetTypeId]

    if (typeConfig) {
      const catConfig = categoryConfig[typeConfig.category]
      return {
        category: typeConfig.category,
        categoryLabel: catConfig?.label || typeConfig.category,
        categoryColor: catConfig?.color || '#6c757d',
        categoryIcon: catConfig?.icon || 'FileText',
        facetLabel: typeConfig.label,
        facetColor: typeConfig.color,
        facetIcon: typeConfig.icon,
        createsEntity: typeConfig.createsEntity,
        defaultEntityType: typeConfig.defaultEntityType,
        allowedEntityTypes: typeConfig.allowedEntityTypes,
      }
    }

    // Fallback for unknown types
    return {
      category: 'insights',
      categoryLabel: 'Insights',
      categoryColor: '#6c757d',
      categoryIcon: 'MessageCircle',
      facetLabel: facetTypeId.replace(/_/g, ' '),
      facetColor: '#6c757d',
      facetIcon: 'FileText',
      createsEntity: false,
      defaultEntityType: null,
      allowedEntityTypes: null,
    }
  }, [facetTypeConfig, categoryConfig])

  /**
   * Get the default entity type for a facet type
   */
  const getEntityTypeForFacet = useCallback((facetTypeId: string): string | null => {
    return facetTypeConfig[facetTypeId]?.defaultEntityType || null
  }, [facetTypeConfig])

  /**
   * Check if a facet type should create entities
   */
  const shouldCreateEntity = useCallback((facetTypeId: string): boolean => {
    return facetTypeConfig[facetTypeId]?.createsEntity ?? true
  }, [facetTypeConfig])

  /**
   * Get all categories sorted by sortOrder
   */
  const getSortedCategories = useCallback(() => {
    return Object.values(categoryConfig).sort((a, b) => a.sortOrder - b.sortOrder)
  }, [categoryConfig])

  /**
   * Group facet types by category
   */
  const getFacetTypesByCategory = useCallback(() => {
    const result: Record<string, string[]> = {}

    Object.entries(facetTypeConfig).forEach(([typeId, config]) => {
      if (!result[config.category]) {
        result[config.category] = []
      }
      result[config.category].push(typeId)
    })

    return result
  }, [facetTypeConfig])

  return {
    // State
    ...state,
    categoryConfig,
    facetTypeConfig,

    // Actions
    initializeConfig,
    resetConfig,
    refresh,

    // Helpers
    getFacetStyle,
    getEntityTypeForFacet,
    shouldCreateEntity,
    getSortedCategories,
    getFacetTypesByCategory,
  }
}
